"use client"

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award, RotateCcw, Users, Target, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/language-provider";
import { getApiUrl } from "@/lib/api-url";

interface LeaderboardEntry {
  playerName: string;
  score: number;
  totalPanels: number;
  timestamp: Date;
  rank: number;
}

interface SnakeStats {
  totalGames: number;
  highestScore: number;
  averageScore: number;
  uniquePlayers: number;
}

export interface SnakeLeaderboardHandle {
  refresh: () => Promise<void>;
}

interface SnakeLeaderboardProps {
  className?: string;
}

export const SnakeLeaderboard = forwardRef<SnakeLeaderboardHandle, SnakeLeaderboardProps>(function SnakeLeaderboard({ className }, ref) {
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [stats, setStats] = useState<SnakeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(getApiUrl('/api/snake/leaderboard?limit=10'));
      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Erreur API:', response.status, errorText);
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Convertir les timestamps en objets Date
      const formattedLeaderboard = data.leaderboard.map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }));
      
      setLeaderboard(formattedLeaderboard);
      setStats(data.stats);
    } catch (error) {
      // console.error('Erreur lors du chargement du classement:', error);
      toast.error(t('snake.loadError') || 'Erreur de chargement');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLeaderboard();
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchLeaderboard
  }));

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            {t('snake.title')}
          </CardTitle>
          <CardDescription>
            {t('snake.description')}...
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[150px]" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} id="snake-leaderboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              {t('snake.title')}
            </CardTitle>
            <CardDescription>
              {t('snake.description')}
            </CardDescription>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            disabled={isRefreshing}
          >
            <RotateCcw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Statistiques globales */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-muted rounded-lg">
              <Users className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <div className="text-2xl font-bold">{formatNumber(stats.uniquePlayers)}</div>
              <div className="text-xs text-muted-foreground">{t('snake.players')}</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <Target className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <div className="text-2xl font-bold">{formatNumber(stats.totalGames)}</div>
              <div className="text-xs text-muted-foreground">{t('snake.games')}</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <Trophy className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
              <div className="text-2xl font-bold">{formatNumber(stats.highestScore)}</div>
              <div className="text-xs text-muted-foreground">{t('snake.record')}</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-purple-500" />
              <div className="text-2xl font-bold">{formatNumber(stats.averageScore)}</div>
              <div className="text-xs text-muted-foreground">{t('snake.average')}</div>
            </div>
          </div>
        )}

        {/* Classement */}
        {leaderboard.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>{t('snake.noScores')}</p>
            <p className="text-sm mt-2">{t('snake.firstScore')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((entry) => (
              <div
                key={`${entry.playerName}-${entry.timestamp.getTime()}`}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-muted/50 ${
                  entry.rank <= 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent dark:from-yellow-950/20' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {getRankIcon(entry.rank)}
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {entry.playerName}
                      {entry.rank === 1 && <Badge variant="secondary" className="text-xs">{t('snake.champion')}</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatNumber(entry.totalPanels)} {t('snake.panels')} • {formatDate(entry.timestamp)}
                    </div>
                  </div>
                </div>
                                  <div className="text-right">
                    <div className="text-xl font-bold text-primary">
                      {formatNumber(entry.score)}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('snake.points')}</div>
                  </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
});
